import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import PcIcon from "@rsuite/icons/Pc";
import VisibleIcon from "@rsuite/icons/Visible";
import UnvisibleIcon from "@rsuite/icons/Unvisible";
import LocationIcon from "@rsuite/icons/Location";
import { EditSystem } from "./Modal/EditSystem";
import { UserContext } from "../../_context/UserContext";
import Card from "../../../../ui/Card/Card";
import Badge from "../../../../ui/Badge/Badge";
import SectionRibbon from "../../../../ui/SectionRibbon/SectionRibbon";
import NoDataState from "../../../../ui/NoDataState/NoDataState";
import Loader from "../../../../ui/Loader/Loader";
import { colors } from "../../../../ui/theme/colors";

export const CustomerSystemsListFC = (props) => {
  const [systems, setSystems] = useState([]);
  const [activeSystems, setActiveSystems] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [update, setUpdate] = useState(false);
  const [loadingSystems, setLoadingSystems] = useState(true);
  const { userRoles } = useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();

  const getSystemsList = async () => {
    setLoadingSystems(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const siteRes = await axiosInstance({
        url: `/customer/systems/${props.customer}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setSystems(siteRes?.data[0]);
      setActiveSystems(
        siteRes?.data[0].filter((system) => {
          return system.show_on_website;
        }),
      );
    } catch (error) {
      console.error(error);
      setLoadingSystems(false);
    }
    setLoadingSystems(false);
  };

  const getSiteList = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const siteRes = await axiosInstance({
        url: `/customer/sites/${props.customer}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setSiteList(
        siteRes?.data[0].map((site) => {
          return { value: site.id, label: `${site.id}: ${site.name}` };
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const initSites = () => {
    setSites([...new Set(systems.map((system) => system.site_name))]);
  };

  const getSystemsForSite = (siteName) => {
    return systems.filter((system) => system.site_name === siteName);
  };

  const getVisibilityVariant = (isVisible) => {
    return isVisible ? "success" : "error";
  };

  useEffect(() => {
    getSystemsList();
    getSiteList();
  }, [props.customer, update]);

  useEffect(() => {
    initSites();
  }, [systems]);

  if (loadingSystems) {
    return (
      <Container>
        <Loader content="Loading systems..." centered variant="glass" />
      </Container>
    );
  }

  return (
    <Container>
      <SectionRibbon
        title="Customer Systems"
        subtitle="Manage systems across all sites"
        icon={PcIcon}
        variant="primary"
        rightContent={
          <StatsContainer>
            <StatItem>
              <StatValue>
                {activeSystems.length}/{systems.length}
              </StatValue>
              <StatLabel>Visible Systems</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{sites.length}</StatValue>
              <StatLabel>Sites</StatLabel>
            </StatItem>
          </StatsContainer>
        }
      />

      {sites.length > 0 ? (
        sites.map((siteName, siteIndex) => {
          const siteSystemsList = getSystemsForSite(siteName);

          return (
            <SiteSection key={siteName || "no-site"}>
              <SiteHeader>
                <SiteName>
                  <LocationIcon
                    style={{ marginRight: "8px", fontSize: "18px" }}
                  />
                  {siteName || "No Site Name"}
                </SiteName>
                <SystemCount>
                  <Badge variant="info" size="small">
                    {siteSystemsList.length} systems
                  </Badge>
                </SystemCount>
              </SiteHeader>

              {siteSystemsList.length > 0 ? (
                <SystemsGrid>
                  {siteSystemsList.map((system, index) =>
                    system.id ? (
                      <Card
                        key={system.id}
                        animated
                        visible={true}
                        delay={siteIndex * 100 + index * 50}
                        hoverable
                      >
                        <SystemContent>
                          <SystemHeader>
                            <SystemId>#{system.id}</SystemId>
                            <SystemActions>
                              <VisibilityBadge
                                variant={getVisibilityVariant(
                                  system.show_on_website,
                                )}
                                size="small"
                              >
                                {system.show_on_website ? (
                                  <>
                                    <VisibleIcon
                                      style={{
                                        marginRight: "4px",
                                        fontSize: "12px",
                                      }}
                                    />
                                    Visible
                                  </>
                                ) : (
                                  <>
                                    <UnvisibleIcon
                                      style={{
                                        marginRight: "4px",
                                        fontSize: "12px",
                                      }}
                                    />
                                    Hidden
                                  </>
                                )}
                              </VisibilityBadge>
                              {userRoles.includes("superuser") && (
                                <EditButtonContainer>
                                  <EditSystem
                                    sites={siteList}
                                    system={system}
                                    update={update}
                                    setUpdate={setUpdate}
                                  />
                                </EditButtonContainer>
                              )}
                            </SystemActions>
                          </SystemHeader>

                          <SystemTitle>
                            {[system.manufacturer, system.modality]
                              .filter(Boolean)
                              .join(" - ") || "Unknown System"}
                          </SystemTitle>

                          <SystemDetails>
                            <DetailRow>
                              <DetailLabel>CUID:</DetailLabel>
                              <DetailValue>
                                {system.cus_sys_id || "N/A"}
                              </DetailValue>
                            </DetailRow>

                            <DetailRow>
                              <DetailLabel>Model:</DetailLabel>
                              <DetailValue>{system.model || "N/A"}</DetailValue>
                            </DetailRow>

                            <DetailRow>
                              <DetailLabel>Serial Number:</DetailLabel>
                              <DetailValue>
                                {system.serial_number || "N/A"}
                              </DetailValue>
                            </DetailRow>

                            <DetailRow>
                              <DetailLabel>Room:</DetailLabel>
                              <DetailValue>{system.room || "N/A"}</DetailValue>
                            </DetailRow>
                          </SystemDetails>
                        </SystemContent>
                      </Card>
                    ) : (
                      <Card key={`no-system-${index}`} variant="deactivated">
                        <SystemContent>
                          <NoSystemMessage>No System Data</NoSystemMessage>
                        </SystemContent>
                      </Card>
                    ),
                  )}
                </SystemsGrid>
              ) : (
                <NoDataState
                  icon="⚙️"
                  title="No Systems"
                  message={`No systems found for ${siteName || "this site"}.`}
                />
              )}
            </SiteSection>
          );
        })
      ) : (
        <NoDataState
          icon="🏢"
          title="No Systems Found"
          message="No systems available for this customer."
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const SiteSection = styled.div`
  margin-bottom: 40px;
`;

const SiteLabel = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

const SystemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SystemContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SystemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SystemId = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[600]};
`;

const SystemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const VisibilityBadge = styled(Badge)`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const EditButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SystemTitle = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.4;
`;

const SystemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: ${colors.neutral[500]};
  font-weight: 500;
  min-width: 80px;
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${colors.neutral[700]};
  line-height: 1.4;
  word-break: break-word;
`;

const NoSystemMessage = styled.div`
  text-align: center;
  color: ${colors.neutral[500]};
  font-style: italic;
  padding: 12px;
`;

const SiteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    ${colors.primary[500]}15 0%,
    ${colors.primary[600]}10 100%
  );
  backdrop-filter: blur(10px);
  border: 1px solid ${colors.primary[200]}40;
  border-radius: 12px;
  box-shadow:
    0 4px 6px -1px ${colors.primary[500]}10,
    0 2px 4px -1px ${colors.primary[500]}06;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 8px 15px -3px ${colors.primary[500]}15,
      0 4px 6px -2px ${colors.primary[500]}10;
  }
`;

const SiteName = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  display: flex;
  align-items: center;
`;

const SystemCount = styled.div`
  display: flex;
  align-items: center;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StatValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
